import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import { CartesianGrid, XAxis, YAxis, Legend, LineChart, Line, PieChart, Pie, Cell, Tooltip as RechartsTooltip } from "recharts";
import { Checkbox } from "@shared-components/shadcn/checkbox";
import { Button } from "@shared-components/shadcn/button";
import { formatGranularDateTick } from "@shared-components/core-metrics/metric-utils";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import { type PluginTypeEnumTypes, PluginTypeEnumValues } from "@chat-ai/_database/enums";
import { pluginsMetadata } from "@chat-ai/_database/models/plugins-metadata";
const chartConfig: ChartConfig = {
  count: {
    label: "Plugins count in chats"
  }
};
interface PluginUsageChartProps {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
}
export const PluginUsageChart = t0 => {
  const $ = _c(23);
  const {
    startDate,
    endDate,
    granularity
  } = t0;
  let t1;
  if ($[0] !== endDate || $[1] !== granularity || $[2] !== startDate) {
    t1 = {
      startDate,
      endDate,
      granularity
    };
    $[0] = endDate;
    $[1] = granularity;
    $[2] = startDate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const pluginsQuery = api.coreMetrics.pluginsEnabledPerDay.useQuery(t1);
  let chartData;
  let t2;
  if ($[4] !== pluginsQuery.data) {
    chartData = pluginsQuery.data as PluginDataItem[] ?? [];
    const pluginTotals = {} as Record<PluginTypeEnumTypes, number>;
    PluginTypeEnumValues.forEach(plugin => {
      pluginTotals[plugin] = chartData.reduce((sum, item) => sum + Number(item[plugin] ?? 0), 0);
    });
    t2 = PluginTypeEnumValues.filter(plugin_0 => pluginTotals[plugin_0] > 0).map(plugin_1 => ({
      plugin: plugin_1,
      name: pluginsMetadata(plugin_1).name,
      value: pluginTotals[plugin_1],
      color: pluginsMetadata(plugin_1).metricColor
    }));
    $[4] = pluginsQuery.data;
    $[5] = chartData;
    $[6] = t2;
  } else {
    chartData = $[5];
    t2 = $[6];
  }
  const pieChartData = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = PluginTypeEnumValues.map(_temp);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const [visiblePlugins, setVisiblePlugins] = useState(t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = plugin_2 => {
      setVisiblePlugins(prev => prev.includes(plugin_2) ? prev.filter(p_0 => p_0 !== plugin_2) : [...prev, plugin_2]);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleToggle = t4;
  let t5;
  if ($[9] !== pieChartData) {
    t5 = () => {
      setVisiblePlugins(pieChartData.map(_temp2));
    };
    $[9] = pieChartData;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleEnablePiePlugins = t5;
  let t6;
  if ($[11] !== chartData) {
    t6 = chartData.map(_temp3);
    $[11] = chartData;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const normalizedChartData = t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h2 className="mb-4">Plugin usage per day</h2>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== chartData.length || $[15] !== granularity || $[16] !== handleEnablePiePlugins || $[17] !== normalizedChartData || $[18] !== pieChartData || $[19] !== visiblePlugins) {
    t8 = chartData.length !== 0 && <><div className="mb-4 flex flex-wrap gap-3">{PluginTypeEnumValues.map(plugin_4 => <label key={plugin_4} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50"><Checkbox checked={visiblePlugins.includes(plugin_4)} onCheckedChange={() => handleToggle(plugin_4)} className="accent-current" /><span>{pluginsMetadata(plugin_4).name}</span></label>)}<Button onClick={handleEnablePiePlugins}>{"Enable plugins for selected time"}</Button></div><div className="mt-8 flex h-[300px] w-full items-start"><div className="flex h-full flex-1 flex-col"><ChartContainer config={chartConfig} className="aspect-auto h-full flex-1"><LineChart data={normalizedChartData} margin={{
              top: 10,
              right: 30,
              left: 20,
              bottom: 10
            }} height={260}><CartesianGrid vertical={false} /><XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} fill="#006e96" minTickGap={32} tickFormatter={value => formatGranularDateTick(value, granularity)} /><YAxis yAxisId="count" interval={1} orientation="left" /><ChartTooltip content={<ChartTooltipContent className="w-[150px]" nameKey="date" labelFormatter={_temp4} />} />{visiblePlugins.map(_temp5)}</LineChart></ChartContainer><div className="mt-2 flex h-[32px] flex-wrap items-center justify-center">{visiblePlugins.map(_temp6)}</div></div></div><div className="flex items-center justify-center"><PieChart width={600} height={400}><Pie data={pieChartData.filter(entry_0 => visiblePlugins.includes(entry_0.plugin))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={_temp7}>{pieChartData.map(_temp8)}</Pie><RechartsTooltip /><Legend /></PieChart></div></>;
    $[14] = chartData.length;
    $[15] = granularity;
    $[16] = handleEnablePiePlugins;
    $[17] = normalizedChartData;
    $[18] = pieChartData;
    $[19] = visiblePlugins;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <div>{t7}{t8}</div>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
function _temp(p) {
  return p;
}
function _temp2(entry) {
  return entry.plugin;
}
function _temp3(item_0) {
  const filled = {
    ...item_0
  };
  PluginTypeEnumValues.forEach(plugin_3 => {
    if (typeof filled[plugin_3] !== "number") {
      filled[plugin_3] = 0;
    }
  });
  return filled;
}
function _temp4(value_0) {
  return value_0;
}
function _temp5(plugin_5) {
  return <Line key={plugin_5} yAxisId="count" type="monotone" dataKey={plugin_5} name={pluginsMetadata(plugin_5).name} stroke={pluginsMetadata(plugin_5).metricColor} strokeWidth={2} dot={false} />;
}
function _temp6(plugin_6) {
  return <span key={plugin_6} className="mr-4 flex items-center text-sm"><span className="mr-1 inline-block h-3 w-3 rounded-full" style={{
      background: pluginsMetadata(plugin_6).metricColor
    }} />{pluginsMetadata(plugin_6).name}</span>;
}
function _temp7(t0) {
  const {
    name,
    value: value_1
  } = t0;
  return `${name}: (${value_1})`;
}
function _temp8(entry_1, idx) {
  return <Cell key={`cell-${idx}`} fill={entry_1.color} />;
}